import { StatusChip } from "../../components/atoms/StatusChip";
import { GroupBox } from "../../components/atoms/GroupBox";
import { MySwitch } from "../../components/atoms/checkbox";
import { MultilineRenderer } from "../../components/molecules/MultilineRenderer";
import MultilineEditor from "../../components/molecules/MultilineEditor";
import { RankMenu } from "../../components/molecules/RankMenu";
import { EndCollectionSelect } from "../../components/molecules/EndCollectionSelect";
import { dateFormatOptions, dateTimeFormatter } from "../common";
import type { Rubric, RubricVersion } from "../../types/rubric";

export const sortColumnDefs = (columnDefs, directoryType) => {
  let orderList;
  switch (directoryType) {
    case "qa":
      orderList = Object.keys(qaColumns);
      break;
    case "collection":
      orderList = Object.keys(queueListColumns);
      break;
    case "distribution":
      orderList = Object.keys(coderDistributionColumns);
      break;
    case "case_samples":
      orderList = Object.keys(caseSamplesColumns);
      break;
    case "coder_samples":
      orderList = Object.keys(caseSamplesColumns);
      break;
    case "verification_samples":
      orderList = Object.keys(caseSamplesColumns);
      break;
    case "cqa_samples":
      orderList = Object.keys(caseSamplesColumns);
      break;
  }
  return columnDefs.sort((a, b) => {
    const indexA = orderList.indexOf(a.field);
    const indexB = orderList.indexOf(b.field);
    return indexA - indexB;
  });
};

export const queueListColumns = {
  dragdrop: { width: 3 },
  rank: { width: 6 },
  name: { width: 17 },
  availability: { width: 10 },
  description: { width: 25 },
  last30days: { width: 9 },
  remaining_cases: { width: 10 },
  coder_assigned: { width: 10 },
  menu: { width: 4 },
  delete: { width: 4 },
};

export const coderDistributionColumns = {
  availability: { width: 10 },
  name: { width: 13 },
  groups: { width: 15 },
  // manager: {},
  worker_status: { width: 7 },
  queue: { width: 38 },
  max_cases: { width: 10 },
  assigned_cases: { width: 7 },
};

export const qaColumns = {
  name: { width: 25 },
  days_in_qa: { width: 25 },
  end_qa_date: { width: 25 },
};

export const caseSamplesColumns = {
  dragdrop: { width: 3 },
  rank: { width: 8 },
  name: { width: 16 },
  project_configuration: { width: 20 },  // Derives rubric_name and rubric_version columns
  availability: { width: 7 },
  start_date: { width: 11 },
  end_date: { width: 11 },
  max_cases: { width: 8 },
  manager: { width: 12 },
  menu: { width: 4 },
  delete: { width: 5 },
};

export const updatedDistributionColumnDefs = (
  columnDefs,
  client,
  isUpdatePermitted,
  showMaxCases,
  distributionProps = {}
) => {

  // create a new array to store the updated column definitions
  const updatedColumnDefs = [];

  // loop through each column definition
  for (let i = 0; i < columnDefs.length; i++) {
    const columnDef = columnDefs[i];

    // check if the column definition needs to be updated based on its field name
    switch (columnDef.field) {
      case "name":
        updatedColumnDefs.push({
          ...columnDef,
          sortable: true,
          width: 200,
          cellStyle: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          },
        });
        break;
      case "availability":
        updatedColumnDefs.push({
          ...columnDef,
          cellRenderer: MySwitch,
          cellRendererParams: {
            client,
          },
          sortable: false,
          width: 200,
          cellStyle: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          },
        });
        break;
      case "groups":
        updatedColumnDefs.push({
          ...columnDef,
          cellRenderer: GroupBox,
          cellRendererParams: {
            objectValue: "groups",
            arrayName: "groupsArr",
            client,
          },
          autoHeight: true,
          sortable: false,
          cellStyle: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          },
        });
        break;
      case "worker_status":
        updatedColumnDefs.push({
          ...columnDef,
          cellRenderer: StatusChip,
          sortable: false,
          width: 200,
          cellStyle: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          },
        });
        break;
      case "queue":
        updatedColumnDefs.push({
          ...columnDef,
          cellRenderer: MultilineRenderer,
          cellRendererParams: {
            objectValue: "queue",
            arrayName: "queueArr",
            addIcon: isUpdatePermitted ?? false,
            client,
            distributionProps
          },
          sortable: false,
          width: 300,
          entityType: "",
          autoHeight: true,
        });
        break;
      case "max_cases":
        if (showMaxCases)
          updatedColumnDefs.push({
            ...columnDef,
            cellEditor: MultilineEditor,
            cellEditorParams: {
              client,
              suppressBorder: true,
              suppressOutline: true,
            },
            cellRendererParams: {
              objectValue: "max_cases",
              arrayName: "queueArr",
            },
            cellRenderer: MultilineRenderer,
            sortable: true,
            width: 200,
            autoHeight: true,
            editable: isUpdatePermitted ?? false,
            cellStyle: { backgroundColor: "#F7FDFF" },
          });
        break;
      case "assigned_cases":
        updatedColumnDefs.push({
          ...columnDef,
          cellRenderer: MultilineRenderer,
          sortable: false,
          width: 200,
          autoHeight: true,
          cellRendererParams: {
            objectValue: "assigned_cases",
            arrayName: "queueArr",
            client,
            deleteIcon: isUpdatePermitted ?? false,
          },
        });
        break;
      default:
        updatedColumnDefs.push(columnDef);
        break;
    }
  }

  return updatedColumnDefs;
};

export const updatedCollectionColumnDefs = (
  columnDefs,
  handleRankSelection,
  client,
  availableRecordsCount,
  setAvailableRecordsCount,
) => {
  // create a new array to store the updated column definitions
  const updatedColumnDefs = [];
  for (let i = 0; i < columnDefs.length; i++) {
    const columnDef = columnDefs[i];

    // check if the column definition needs to be updated based on its field name
    switch (columnDef.field) {
      case "menu":
        updatedColumnDefs.push({
          ...columnDef,
          cellRenderer: RankMenu,
          cellRendererParams: {
            handleRankSelection,
          },
        });
        break;
      case "last30days":
        updatedColumnDefs.push({
          ...columnDef,
          valueGetter: (params) => {
            return parseInt(params.data?.last30days, 10);
          },
        });
        break;
      case "remaining_cases":
        updatedColumnDefs.push({
          ...columnDef,
          valueGetter: (params) => {
            return parseInt(params.data?.remaining_cases, 10);
          },
        });
        break;
      case "coder_assigned":
        updatedColumnDefs.push({
          ...columnDef,
          valueGetter: (params) => {
            return parseInt(params.data?.coder_assigned, 10);
          },
        });
        break;
      case "availability":
        updatedColumnDefs.push({
          ...columnDef,
          cellRenderer: MySwitch,
          cellRendererParams: {
            client,
            availableRecordsCount,
            setAvailableRecordsCount,
          },
          cellStyle: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          },
        });
      break;
      default:
        updatedColumnDefs.push(columnDef);
        break;
    }
  }
  return updatedColumnDefs;
};

export const updatedQAColumnDefs = (columnDefs, client) => {
  // create a new array to store the updated column definitions
  const updatedColumnDefs = [];
  for (let i = 0; i < columnDefs.length; i++) {
    const columnDef = columnDefs[i];

    // check if the column definition needs to be updated based on its field name
    switch (columnDef.field) {
      case "name":
        updatedColumnDefs.push({
          ...columnDef,
          headerName: "Rule Target",
          valueGetter: (params) => {
            const firstName = params.data.first_name;
            const lastName = params.data.last_name;
            return `${firstName} ${lastName}`;
          },
          sortable: false,
        });
        break;
      case "days_in_qa":
        updatedColumnDefs.push({
          ...columnDef,
          headerName: "End Collection",
          cellRenderer: EndCollectionSelect,
          cellRendererParams: {
            client,
          },
          sortable: false,
        });
        break;
      case "end_qa_date":
        updatedColumnDefs.push({
          ...columnDef,
          headerName: "End Time",
          valueGetter: (params) => {
            const endQaDate = new Date(params.data?.end_qa_date);
            const currentDate = new Date();

            if (endQaDate > currentDate) {
              return endQaDate.toLocaleDateString("en-US", dateFormatOptions);
            } else {
              return ""; // Return an empty string if the date is not in the future
            }
          },
          sortable: false,
        });
        break;
      default:
        updatedColumnDefs.push(columnDef);
        break;
    }
  }
  return updatedColumnDefs;
};

export const updatedWorklistColumnDefs = (columnDefs) => {
  // create a new array to store the updated column definitions
  const updatedColumnDefs = [];
  for (let i = 0; i < columnDefs?.length; i++) {
    const columnDef = columnDefs[i];

    // check if the column definition needs to be updated based on its field name
    switch (columnDef.field) {
      case "datetime_of_service":
        updatedColumnDefs.push({
          ...columnDef,
          valueGetter: (params) => {
            if (
              !(
                params.data?.datetime_of_service && params.data?.tenant_timezone
              )
            )
              return "N/A";
            const timestamp = params.data?.datetime_of_service;
            const targetTimezone = params.data?.tenant_timezone;
            // Create a Date object from the timestamp
            const date = new Date(timestamp);
            // Create a DateTimeFormat instance with the target timezone and timezone abbreviation
            const formatter = new Intl.DateTimeFormat("en-US", {
              timeZone: targetTimezone,
              hour: "numeric",
              minute: "numeric",
              hour12: true, // Use 24-hour format
              timeZoneName: "short",
            });
            // Format the date to display the time and timezone abbreviation in the target timezone
            return formatter.format(date);
          },
        });
        break;
      case "submitted_date":
        updatedColumnDefs.push({
          ...columnDef,
          valueGetter: (params) => {
            if (
              !(
                params.data?.submitted_date && params.data?.tenant_timezone
              )
            )
              return "N/A";
            const timestamp = params.data?.submitted_date + 'Z';
            const targetTimezone = params.data?.tenant_timezone;
            // Create a Date object from the timestamp
            const date = new Date(timestamp);
            // Create a DateTimeFormat instance with the target timezone and timezone abbreviation
            return dateTimeFormatter(date, targetTimezone);
          },
        });
        break;
      case "sent_to_qa":
        updatedColumnDefs.push({
          ...columnDef,
          valueGetter: (params) => {
            if (
              !(
                params.data?.sent_to_qa && params.data?.tenant_timezone
              )
            )
              return "N/A";
            const timestamp = params.data?.sent_to_qa + 'Z';
            const targetTimezone = params.data?.tenant_timezone;
            // Create a Date object from the timestamp
            const date = new Date(timestamp);
            // Create a DateTimeFormat instance with the target timezone and timezone abbreviation
            return dateTimeFormatter(date, targetTimezone);
          },
        });
        break;
      case "sent_for_review":
        updatedColumnDefs.push({
          ...columnDef,
          valueGetter: (params) => {
            if (
              !(
                params.data?.sent_for_review && params.data?.tenant_timezone
              )
            )
              return "N/A";
            const timestamp = params.data?.sent_for_review + 'Z';
            const targetTimezone = params.data?.tenant_timezone;
            // Create a Date object from the timestamp
            const date = new Date(timestamp);
            // Create a DateTimeFormat instance with the target timezone and timezone abbreviation
            return dateTimeFormatter(date, targetTimezone);
          },
        });
        break;
      case "owner":
        updatedColumnDefs.push({
          ...columnDef,
          headerName: "Coder",
        });
        break;
      default:
        updatedColumnDefs.push(columnDef);
        break;
    }
  }
  return updatedColumnDefs;
};

export const updatedCaseSamplesColumnDefs = (
  columnDefs,
  handleRankSelection,
  client,
  rubricsMap?: Map<number, Rubric>,
  rubricVersionsMap?: Map<number, RubricVersion>
) => {
  // create a new array to store the updated column definitions
  const updatedColumnDefs = [];
  for (let i = 0; i < columnDefs.length; i++) {
    const columnDef = columnDefs[i];

    // check if the column definition needs to be updated based on its field name
    switch (columnDef.field) {
      case "menu":
        updatedColumnDefs.push({
          ...columnDef,
          cellRenderer: RankMenu,
          cellRendererParams: {
            handleRankSelection,
          },
        });
        break;
      case "name":
        updatedColumnDefs.push({
          ...columnDef,
          headerName: "Rule Name",
        });
        break;
      case "max_cases":
        updatedColumnDefs.push({
          ...columnDef,
          headerName: "Volume",
        });
        break;
      case "start_date":
        updatedColumnDefs.push({
          ...columnDef,
          headerName: "Start Time",
          valueGetter: (params) => {
            if (params.data?.start_date) {
              const startQaDate = new Date(params.data?.start_date);
              if (startQaDate) {
                return startQaDate.toLocaleDateString("en-US", dateFormatOptions);
              } else {
                return "";
              }
            }
          },
          sortable: false,
        });
        break;
      case "end_date":
        updatedColumnDefs.push({
          ...columnDef,
          headerName: "End time",
          valueGetter: (params) => {
            if (params.data?.end_date) {
              const endQaDate = new Date(params.data?.end_date);
              if (endQaDate) {
                return endQaDate.toLocaleDateString("en-US", dateFormatOptions);
              } else {
                return "";
              }
            }
          },
        });
        break;
      case "availability":
        updatedColumnDefs.push({
          ...columnDef,
          headerName: "Active",
          cellRenderer: MySwitch,
          cellRendererParams: {
            client,
          },
          sortable: false,
          cellStyle: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          },
        });
        break;
      case "group_id":
      case "manager":
        updatedColumnDefs.push({
          ...columnDef,
          headerName: "Recipient",
          valueGetter: (params) => {
            if (params.data?.manager) {
              return params.data?.manager?.label ?? "";
            } else if (params.data?.group_id) {
              return params.data?.group_id.label ?? "";
            }
          }
        });
        break;
      case "project_configuration":
        // Push Rubric column derived from project_configuration.rubricId
        updatedColumnDefs.push({
          field: "rubric_name",
          headerName: "Rubric",
          valueGetter: (params) => {
            const rubricId = params.data?.project_configuration?.rubricId;
            if (rubricId && rubricsMap) {
              return rubricsMap.get(rubricId)?.name ?? "";
            }
            return "";
          },
          sortable: true,
          width: 150,
        });
        // Push Rubric Version column derived from project_configuration.rubricVersionId
        updatedColumnDefs.push({
          field: "rubric_version",
          headerName: "Rubric Version",
          valueGetter: (params) => {
            const rubricVersionId = params.data?.project_configuration?.rubricVersionId;
            if (rubricVersionId && rubricVersionsMap) {
              const version = rubricVersionsMap.get(rubricVersionId);
              if (version) {
                return `${version.majorVersion}.${version.minorVersion}`;
              }
            }
            return "";
          },
          sortable: true,
          width: 120,
        });
        break;
      default:
        updatedColumnDefs.push(columnDef);
        break;
    }
  }
  return updatedColumnDefs;
};
